/**
 * Reasons a sign-in is refused.
 *
 * Pure constants with no imports, so browser code that labels audit events can
 * take them without pulling token verification or identity binding into the bundle.
 *
 * @module server/auth/denial-reasons
 */

/**
 * The enumerable reasons an authentication attempt can be denied. Kept as an
 * explicit union so callers can branch on the exact denial without inspecting
 * error messages.
 *
 * - `"invalid-token"`: the token failed verification (invalid/malformed/expired).
 * - `"unknown-user"`: no backend `User` matches the verified principal.
 * - `"deactivated"`: the matched backend `User` is deactivated.
 */
export const AUTH_DENIAL_REASONS = ["invalid-token", "unknown-user", "deactivated"] as const;

export type AuthDenialReason = (typeof AUTH_DENIAL_REASONS)[number];

/**
 * Why a first-login claim was refused. Audited in full; the caller sees only
 * `unknown-user`, so the reason cannot be used to probe records.
 *
 * - `"no-invite"`: no live invitation matches the verified email.
 * - `"ambiguous-invite"`: more than one organisation invited this address.
 * - `"tenant-missing"`: the invited tenant no longer exists.
 * - `"tenant-unavailable"`: the tenant is deactivated or suspended.
 * - `"cross-tenant"`: the tenant is bound to a different directory.
 * - `"identity-conflict"`: the account is anchored to a different principal.
 * - `"alias-outside-tenant"`: the token's address is not the account's own, and
 *   the account holds something beyond the inviting tenant.
 * - `"alias-address-taken"`: the token's address is not the account's own, and
 *   a different account holds it.
 * - `"platform-account"`: the account holds a platform role. Its holder signs in
 *   by the object id the group sync records, never by an invited address.
 * - `"invite-withdrawn"`: the invitation was withdrawn, or moved to another
 *   address, while the claim was being made.
 */
export const INVITE_CLAIM_DENIAL_REASONS = [
  "no-invite",
  "ambiguous-invite",
  "tenant-missing",
  "tenant-unavailable",
  "cross-tenant",
  "identity-conflict",
  "alias-outside-tenant",
  "alias-address-taken",
  "platform-account",
  "invite-withdrawn",
] as const;

export type InviteClaimDenialReason = (typeof INVITE_CLAIM_DENIAL_REASONS)[number];
